import { memo, useEffect, useCallback, useState, Fragment, useMemo } from 'react';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { Alert } from 'foundations-components/transitional/components/Alert';
import { isFailed, isSucceeded, isStarted } from 'conversations-async-data/async-data/operators/statusComparators';
import { getData } from 'conversations-async-data/async-data/operators/getters';
import ThreeDotIconButton from './ThreeDotIconButton';
import debounce from 'react-utils/debounce';
import { CardSection, CardWrapper } from 'foundations-components/transitional/components/Card';
import { Link } from 'foundations-components/transitional/components/Link';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const MessageHistoryToggleButton = ({
  historyContent,
  onClickTracker,
  containerClass,
  msgHistoryId,
  isExpandedHistoryByDefault = false
}) => {
  const [showMessageHistory, setShowMessageHistory] = useState(false);

  // Reset the history content if user moves on to another message
  useEffect(() => {
    setShowMessageHistory(isExpandedHistoryByDefault);
  }, [msgHistoryId, isExpandedHistoryByDefault]);
  const toggleHistoryContent = useMemo(() => debounce(() => {
    setShowMessageHistory(prevShowMessageHistory => {
      onClickTracker(prevShowMessageHistory);
      return !prevShowMessageHistory;
    });
  }, 300), [onClickTracker]);

  // Finds the correct container for the history based off of a provided class
  // and moves the history to the bottom of the container
  const moveHistoryToEnd = useCallback(() => {
    const referenceNode = document.querySelector('div.DraftEditor-root .public-DraftEditor-content');
    if (referenceNode) {
      const newNode = document.querySelector(`div.${containerClass}`);
      referenceNode.appendChild(newNode);
    }
  }, [containerClass]);
  useEffect(() => {
    moveHistoryToEnd();
    return () => {
      const history = document.querySelector(`div.${containerClass}`);
      if (history) history.remove();
    };
  }, [moveHistoryToEnd, containerClass]);
  return (
    /*#__PURE__*/
    // NOTE - Do not remove the extra div
    // React needs something to remove from the original parent node in the DOM
    _jsx("div", {
      children: /*#__PURE__*/_jsxs("div", {
        className: containerClass,
        contentEditable: false,
        children: [/*#__PURE__*/_jsx(ThreeDotIconButton, {
          onClickHandler: toggleHistoryContent
        }), showMessageHistory && historyContent]
      })
    })
  );
};
MessageHistoryToggleButton.displayName = 'MessageHistoryToggleButton';
const MessageHistory = /*#__PURE__*/memo(({
  fetchHistoryHtml,
  // retry function to refetch history if failed
  asyncHistoryHtml // async data record for the history fetch
}) => {
  const renderFailBanner = useCallback(() => {
    return /*#__PURE__*/_jsx(Alert, {
      type: "danger",
      titleText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: 'conversations-thread-view.editors.previousMessageHistory.fetchFailedMessage'
      }),
      children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: 'conversations-thread-view.editors.previousMessageHistory.fetchFailedTryAgain_jsx',
        options: {
          onClick: () => fetchHistoryHtml()
        },
        elements: {
          Link
        }
      })
    });
  }, [fetchHistoryHtml]);
  const renderMessageHistory = useCallback(() => {
    return /*#__PURE__*/_jsx(CardWrapper, {
      compact: true,
      marginBlockStart: "250",
      padding: "250",
      children: /*#__PURE__*/_jsxs(CardSection, {
        padding: "100",
        maxBlockSize: "200px",
        overflow: "scroll",
        children: [isFailed(asyncHistoryHtml) && renderFailBanner(), isSucceeded(asyncHistoryHtml) && /*#__PURE__*/_jsx("div", {
          "data-test-id": "message-history-content"
          /* eslint-disable-next-line react/no-danger */,
          dangerouslySetInnerHTML: {
            __html: getData(asyncHistoryHtml) || I18n.text('conversations-thread-view.editors.previousMessageHistory.noHistory')
          }
        }), isStarted(asyncHistoryHtml) && /*#__PURE__*/_jsx(LoadingSpinner, {})]
      })
    });
  }, [renderFailBanner, asyncHistoryHtml]);
  return /*#__PURE__*/_jsx(Fragment, {
    children: renderMessageHistory()
  });
});
MessageHistory.displayName = 'MessageHistory';
export default MessageHistory;